import React, { useCallback, useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import { crearPersona, resumenPersonas, listarPersonas, actualizarPersona, sincronizarPersonaContifico } from "../../../../services/personasService";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";
import { isModuloHabilitado } from "../../../../utils/modulosSistema";

// ─── Constantes ────────────────────────────────────────────────────
const TIPO_DOC_LABEL = { cedula: "Cédula", ruc: "RUC", pasaporte: "Pasaporte" };
const TIPO_LABEL     = { N: "Natural", J: "Jurídica", I: "Sin ID" };
const PAGE_SIZE      = 25;
const MIN_ROWS       = 10;

const KPI_CARDS = [
  { key: "total",         label: "Total",             icon: "bi-people",       accent: "",        tip_doc: null },
  { key: "tipo_ruc",      label: "Empresas / RUC",    icon: "bi-buildings",    accent: "info",    tip_doc: "ruc" },
  { key: "con_contifico", label: "En Contífico",      icon: "bi-cloud-check",  accent: "success", tip_doc: null },
  { key: "sin_contifico", label: "Sin sincronizar",   icon: "bi-cloud-slash",  accent: "neutral", tip_doc: null },
];

// ─── Sub-componente: KPI cards ──────────────────────────────────────
function ResumenCards({ resumen, activeKey, onFilter }) {
  return (
    <div className="management-card mb-4">
      <div className="est-summary-grid">
        {KPI_CARDS.map((card) => {
          const isActive = activeKey === card.key;
          const accentClass = card.accent ? `accent-${card.accent}` : "";
          return (
            <div key={card.key} className="w-100">
              <div
                className={`management-stat-card est-summary-card h-100 ${accentClass} ${isActive ? "active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => onFilter(card.key)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFilter(card.key); } }}
              >
                <div className="management-stat-icon">
                  <i className={`bi ${card.icon}`} />
                </div>
                <div>
                  <p className="management-stat-label mb-1">{card.label}</p>
                  <div className="management-stat-value">{resumen[card.key] ?? 0}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Sub-componente: Tabla ──────────────────────────────────────────
function PersonasTable({ personas, loading, onEdit, onSync, syncingId, canEdit, contificoActivo }) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos,    setMenuPos]    = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (!personas.length) {
    return (
      <div className="management-card dashboard-empty text-center py-5">
        <i className="bi bi-people dashboard-empty-icon d-block mb-3" />
        <h5 className="fw-semibold mb-1">Sin personas registradas.</h5>
        <p className="text-muted mb-0">Aparecerán aquí cuando se registre un pago a nombre de un tercero.</p>
      </div>
    );
  }

  const emptyRows = Math.max(0, MIN_ROWS - personas.length);

  return (
    <div className="management-card dashboard-table-card estudiantes-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <p className="dashboard-label mb-1">Listado</p>
          <h6 className="mb-0">Responsables de pago registrados</h6>
        </div>
      </div>
      <div className="management-table-wrapper" style={{ maxHeight: "60vh" }}>
        <table className="table align-middle management-table estudiantes-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th>#</th>
              <th>Nombre / Razón social</th>
              <th>Documento</th>
              <th>Contacto</th>
              <th>Pagos</th>
              {canEdit && <th className="text-end">Acciones</th>}
            </tr>
          </thead>
          <tbody ref={wrapRef}>
            {personas.map((p, idx) => {
              const isMenuOpen = openMenuId === p.id;
              return (
                <tr key={p.id}>
                  <td className="fw-semibold">{idx + 1}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold">{p.razon_social}</span>
                      {contificoActivo && p.contifico_persona_id && (
                        <i className="bi bi-cloud-check text-success" title="Sincronizado con Contífico" style={{ fontSize: 13 }} />
                      )}
                    </div>
                    <small className="text-muted">{TIPO_LABEL[p.tipo] || p.tipo}</small>
                  </td>
                  <td>
                    <div>{p.documento}</div>
                    <small className="text-muted">{TIPO_DOC_LABEL[p.tipo_doc] || p.tipo_doc}</small>
                  </td>
                  <td>
                    <div>{p.email || <span className="text-muted">—</span>}</div>
                    {p.telefono && <small className="text-muted">{p.telefono}</small>}
                  </td>
                  <td>
                    <span className="badge rounded-pill bg-light text-dark border fw-semibold">
                      {p.total_pagos ?? 0}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="text-end">
                      <div className="management-actions-wrap">
                        <button
                          type="button"
                          className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isMenuOpen) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setOpenMenuId(isMenuOpen ? null : p.id);
                          }}
                          title="Acciones"
                          aria-expanded={isMenuOpen}
                          disabled={syncingId === p.id}
                        >
                          {syncingId === p.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-three-dots-vertical" />}
                        </button>
                        {isMenuOpen && (
                          <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                            <li>
                              <button
                                type="button"
                                className="menu-item"
                                onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit(p); }}
                              >
                                <i className="bi bi-pencil-square" />
                                Editar
                              </button>
                            </li>
                            {contificoActivo && (
                              <>
                                <li className="menu-divider" />
                                <li>
                                  <button
                                    type="button"
                                    className="menu-item"
                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onSync(p); }}
                                  >
                                    <i className="bi bi-cloud-upload" />
                                    Sincronizar con Contífico
                                  </button>
                                </li>
                              </>
                            )}
                          </ul>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {emptyRows > 0 && Array.from({ length: emptyRows }).map((_, i) => (
              <tr key={`empty-${i}`} className="table-empty-row" aria-hidden="true">
                <td colSpan={canEdit ? 6 : 5}>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Modal de creación ───────────────────────────────────────────────
const FORM_VACIO = { tipo: "N", razon_social: "", documento: "", tipo_doc: "cedula", email: "", telefono: "", direccion: "" };

function CreateModal({ onClose, onCreated }) {
  const [form, setForm]     = useState(FORM_VACIO);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.razon_social.trim() || !form.documento.trim()) {
      showToast({ type: "warning", message: "Nombre y documento son requeridos." });
      return;
    }
    setSaving(true);
    try {
      const nueva = await crearPersona(form);
      showToast({ type: "success", message: `${nueva.razon_social} registrada correctamente.` });
      onCreated(nueva);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al crear persona." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Nueva persona</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Tipo de persona</label>
                  <select className="form-select" value={form.tipo} onChange={(e) => set("tipo", e.target.value)}>
                    <option value="N">Natural</option>
                    <option value="J">Jurídica</option>
                    <option value="I">Sin identificación</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Tipo de documento</label>
                  <select className="form-select" value={form.tipo_doc} onChange={(e) => set("tipo_doc", e.target.value)}>
                    <option value="cedula">Cédula</option>
                    <option value="ruc">RUC</option>
                    <option value="pasaporte">Pasaporte</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Número de documento <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.documento}
                    placeholder={form.tipo_doc === "ruc" ? "Ej: 0990012345001" : "Ej: 0900123456"}
                    onChange={(e) => set("documento", e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Nombre / Razón social <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.razon_social}
                    placeholder="Ej: MARÍA GARCÍA LÓPEZ"
                    onChange={(e) => set("razon_social", e.target.value.toUpperCase())} />
                </div>
                <div className="col-6">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={form.email}
                    onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" value={form.telefono}
                    onChange={(e) => set("telefono", e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label">Dirección</label>
                  <input className="form-control" value={form.direccion}
                    onChange={(e) => set("direccion", e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</> : "Crear persona"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
}

// ─── Modal de edición ────────────────────────────────────────────────
function EditModal({ persona, onClose, onSaved }) {
  const [form, setForm]       = useState({
    razon_social: persona.razon_social,
    tipo_doc:     persona.tipo_doc,
    email:        persona.email    || "",
    telefono:     persona.telefono || "",
    direccion:    persona.direccion|| "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.razon_social.trim()) {
      showToast({ type: "warning", message: "El nombre es requerido." });
      return;
    }
    setSaving(true);
    try {
      const updated = await actualizarPersona(persona.id, form);
      showToast({ type: "success", message: "Persona actualizada correctamente." });
      onSaved(updated);
    } catch {
      showToast({ type: "danger", message: "Error al guardar cambios." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Editar persona</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Nombre / Razón social <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.razon_social}
                    onChange={(e) => setForm(f => ({ ...f, razon_social: e.target.value.toUpperCase() }))} />
                </div>
                <div className="col-6">
                  <label className="form-label">Tipo de documento</label>
                  <select className="form-select" value={form.tipo_doc}
                    onChange={(e) => setForm(f => ({ ...f, tipo_doc: e.target.value }))}>
                    <option value="cedula">Cédula</option>
                    <option value="ruc">RUC</option>
                    <option value="pasaporte">Pasaporte</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="col-6">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" value={form.telefono}
                    onChange={(e) => setForm(f => ({ ...f, telefono: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label">Dirección</label>
                  <input className="form-control" value={form.direccion}
                    onChange={(e) => setForm(f => ({ ...f, direccion: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</> : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
}

// ─── Vista principal ─────────────────────────────────────────────────
export default function PersonasView() {
  const { hasPermission } = usePermissions();
  const canCreate      = hasPermission("personas.crear");
  const canEdit        = hasPermission("personas.editar");
  const contificoActivo = isModuloHabilitado("integracion_contifico");

  const [resumen,    setResumen]    = useState({ total: 0, tipo_ruc: 0, con_contifico: 0, sin_contifico: 0 });
  const [items,      setItems]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [filtros,    setFiltros]    = useState({ busqueda: "", tipo_doc: "" });
  const [activeKpi,  setActiveKpi]  = useState("total");
  const [creando,    setCreando]    = useState(false);
  const [editando,   setEditando]   = useState(null);
  const [syncingId,  setSyncingId]  = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const [data, res] = await Promise.all([
        listarPersonas({
          busqueda: filtros.busqueda || undefined,
          tipo_doc: filtros.tipo_doc || undefined,
          limit: PAGE_SIZE,
          offset,
        }),
        resumenPersonas(),
      ]);
      setItems(data.rows || []);
      setTotal(data.total || 0);
      setResumen(res);
      setLastUpdated(new Date());
    } catch {
      showToast({ type: "danger", message: "Error al cargar personas." });
    } finally {
      setLoading(false);
    }
  }, [filtros, page]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleKpiFilter = (key) => {
    setActiveKpi(key);
    const card = KPI_CARDS.find(c => c.key === key);
    setFiltros(f => ({ ...f, tipo_doc: card?.tip_doc || "" }));
    setPage(1);
  };

  const handleSync = async (persona) => {
    if (syncingId) return;
    setSyncingId(persona.id);
    try {
      const resultado = await sincronizarPersonaContifico(persona.id);
      setItems(prev => prev.map(p =>
        p.id === persona.id ? { ...p, contifico_persona_id: resultado.contifico_persona_id } : p
      ));
      setResumen(prev => ({
        ...prev,
        con_contifico: prev.con_contifico + 1,
        sin_contifico: Math.max(0, prev.sin_contifico - 1),
      }));
      showToast({ type: "success", message: `${persona.razon_social} sincronizado con Contífico.` });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al sincronizar con Contífico." });
    } finally {
      setSyncingId(null);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({ busqueda: "", tipo_doc: "" });
    setActiveKpi("total");
    setPage(1);
  };

  const handleSaved = (updated) => {
    setItems(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    setEditando(null);
    cargar();
  };

  return (
    <section className="management-page management-theme-violet">
      {/* Header */}
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Financiero</p>
          <h4 className="management-title mb-1">Personas</h4>
          <p className="text-muted mb-0">Responsables de pago registrados en el sistema.</p>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          {canCreate && (
            <button type="button" className="btn management-btn-primary" onClick={() => setCreando(true)}>
              <i className="bi bi-plus-circle me-2" />Nueva persona
            </button>
          )}
          <small className="text-muted">
            {lastUpdated ? `Actualizado: ${lastUpdated.toLocaleString()}` : "Cargando..."}
          </small>
        </div>
      </div>

      {/* KPI Cards */}
      <ResumenCards resumen={resumen} activeKey={activeKpi} onFilter={handleKpiFilter} />

      {/* Filtros */}
      <div className="management-card est-filters-card management-filters-card mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-12 col-md-5">
            <label className="form-label management-label mb-1">Buscar</label>
            <input
              type="search"
              className="form-control"
              placeholder="Nombre, documento o email"
              value={filtros.busqueda}
              onChange={(e) => { setFiltros(f => ({ ...f, busqueda: e.target.value })); setPage(1); }}
            />
          </div>
          <div className="col-6 col-md-4">
            <label className="form-label management-label mb-1">Tipo de documento</label>
            <select
              className="form-select"
              value={filtros.tipo_doc}
              onChange={(e) => { setFiltros(f => ({ ...f, tipo_doc: e.target.value })); setActiveKpi("total"); setPage(1); }}
            >
              <option value="">Todos</option>
              <option value="cedula">Cédula</option>
              <option value="ruc">RUC</option>
              <option value="pasaporte">Pasaporte</option>
            </select>
          </div>
          <div className="col-6 col-md-3 d-flex align-items-end">
            <button type="button" className="btn management-btn-primary management-btn-tiny w-100" onClick={limpiarFiltros}>
              <i className="bi bi-eraser-fill me-1" />Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <PersonasTable
        personas={items}
        loading={loading}
        canEdit={canEdit}
        contificoActivo={contificoActivo}
        onEdit={setEditando}
        onSync={handleSync}
        syncingId={syncingId}
      />

      {/* Paginación */}
      <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="text-muted small">
          {total === 0
            ? "Sin resultados"
            : `Mostrando ${(page - 1) * PAGE_SIZE + 1}–${Math.min(total, page * PAGE_SIZE)} de ${total}`}
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
              <i className="bi bi-chevron-left" />
            </button>
            <button className="btn btn-outline-secondary btn-sm" disabled>
              Página {page} / {totalPages}
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal creación */}
      {creando && (
        <CreateModal
          onClose={() => setCreando(false)}
          onCreated={(nueva) => { setCreando(false); cargar(); }}
        />
      )}

      {/* Modal edición */}
      {editando && (
        <EditModal
          persona={editando}
          onClose={() => setEditando(null)}
          onSaved={handleSaved}
        />
      )}
    </section>
  );
}
